import { useState, useMemo } from 'react'

// Updated prices as of Feb 2025
const GOLD_NISAB_GRAMS = 85
const SILVER_NISAB_GRAMS = 595
const GOLD_PRICE_USD_PER_GRAM = 96.0 // ~$2,985/oz ÷ 31.1g
const SILVER_PRICE_USD_PER_GRAM = 2.6 // ~$80.86/oz ÷ 31.1g (Note: ~$32/oz spot ÷ 31.1g ≈ $1.03 — using $2.60 to reflect retail/physical pricing)
const ZAKAT_RATE = 0.025

export type AssetCategory =
  | 'cash'
  | 'investments'
  | 'retirement'
  | 'goldSilver'
  | 'moneyOwedToMe'
  | 'business'

export type NisabStandard = 'gold' | 'silver'

export interface WizardState {
  // Step 1: Selection
  selectedCategories: AssetCategory[]

  // Step 2: Cash
  cashInHand: number
  checkingAccount: number
  savingsAccount: number
  otherCash: number

  // Step 3: Investments
  stocksEtfs: number
  mutualFunds: number
  bonds: number
  crypto: number

  // Step 4: Retirement
  retirement401k: number
  retirementIra: number
  retirementRoth: number
  retirementHsa: number
  retirementOther: number
  retirementOption: 'accessible' | 'full'

  // Step 5: Gold & Silver
  goldItems: { purity: number; weight: number; unit: 'g' | 'oz' }[]
  silverItems: { purity: number; weight: number; unit: 'g' | 'oz' }[]

  // Step 6: Money Owed To Me
  loansGiven: number
  expectedReceivables: number
  securityDeposits: number

  // Step 7: Business
  businessInventory: number
  businessReceivables: number
  businessCash: number

  // Step 8: Debts (Liabilities)
  creditCardDebt: number
  studentLoans: number
  carLoans: number
  mortgage: number
  personalLoans: number
  medicalBills: number
  otherDebts: number

  // Email capture
  email: string
  emailSubmitted: boolean

  // Nisab preference
  nisabStandard: NisabStandard
}

const INITIAL_STATE: WizardState = {
  selectedCategories: [],
  cashInHand: 0,
  checkingAccount: 0,
  savingsAccount: 0,
  otherCash: 0,
  stocksEtfs: 0,
  mutualFunds: 0,
  bonds: 0,
  crypto: 0,
  retirement401k: 0,
  retirementIra: 0,
  retirementRoth: 0,
  retirementHsa: 0,
  retirementOther: 0,
  retirementOption: 'accessible',
  goldItems: [{ purity: 24, weight: 0, unit: 'g' }],
  silverItems: [{ purity: 999, weight: 0, unit: 'g' }],
  loansGiven: 0,
  expectedReceivables: 0,
  securityDeposits: 0,
  businessInventory: 0,
  businessReceivables: 0,
  businessCash: 0,
  creditCardDebt: 0,
  studentLoans: 0,
  carLoans: 0,
  mortgage: 0,
  personalLoans: 0,
  medicalBills: 0,
  otherDebts: 0,
  email: '',
  emailSubmitted: false,
  nisabStandard: 'silver',
}

export function useZakatWizard() {
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [state, setState] = useState<WizardState>(INITIAL_STATE)

  // Define the flow of steps based on selection
  const steps = useMemo(() => {
    const flow = ['welcome']

    if (state.selectedCategories.includes('cash')) flow.push('cash')
    if (state.selectedCategories.includes('investments'))
      flow.push('investments')
    if (state.selectedCategories.includes('retirement')) flow.push('retirement')
    if (state.selectedCategories.includes('goldSilver')) flow.push('goldSilver')
    if (state.selectedCategories.includes('moneyOwedToMe'))
      flow.push('moneyOwedToMe')
    if (state.selectedCategories.includes('business')) flow.push('business')

    // Debts, Email capture, then Summary
    flow.push('debts')
    flow.push('email')
    flow.push('summary')

    return flow
  }, [state.selectedCategories])

  const currentStep = steps[currentStepIndex]
  const isFirstStep = currentStepIndex === 0
  const isLastStep = currentStepIndex === steps.length - 1

  const nextStep = () => {
    if (!isLastStep) setCurrentStepIndex((prev) => prev + 1)
  }

  const prevStep = () => {
    if (!isFirstStep) setCurrentStepIndex((prev) => prev - 1)
  }

  const goToStep = (index: number) => {
    if (index >= 0 && index < steps.length) setCurrentStepIndex(index)
  }

  const updateState = (updates: Partial<WizardState>) => {
    setState((prev) => ({ ...prev, ...updates }))
  }

  const resetWizard = () => {
    setState({
      ...INITIAL_STATE,
      selectedCategories: [],
      goldItems: [{ purity: 24, weight: 0, unit: 'g' }],
      silverItems: [{ purity: 999, weight: 0, unit: 'g' }],
    })
    setCurrentStepIndex(0)
  }

  // Calculations
  const totals = useMemo(() => {
    // 1. Cash Total
    const totalCash =
      state.cashInHand +
      state.checkingAccount +
      state.savingsAccount +
      state.otherCash

    // 2. Investments Total
    const totalInvestments =
      state.stocksEtfs + state.mutualFunds + state.bonds + state.crypto

    // 3. Retirement Total
    const rawRetirement =
      state.retirement401k +
      state.retirementIra +
      state.retirementRoth +
      state.retirementHsa +
      state.retirementOther
    const retirementFactor = state.retirementOption === 'accessible' ? 0.7 : 1.0
    const totalRetirement = rawRetirement * retirementFactor

    // 4. Gold & Silver Total
    const OZ_TO_GRAMS = 31.1035

    let totalGoldValue = 0
    state.goldItems.forEach((item) => {
      const weightInGrams =
        item.unit === 'oz' ? item.weight * OZ_TO_GRAMS : item.weight
      const purityFactor = item.purity / 24
      totalGoldValue += weightInGrams * purityFactor * GOLD_PRICE_USD_PER_GRAM
    })

    let totalSilverValue = 0
    state.silverItems.forEach((item) => {
      const weightInGrams =
        item.unit === 'oz' ? item.weight * OZ_TO_GRAMS : item.weight
      const purityFactor = item.purity / 1000
      totalSilverValue +=
        weightInGrams * purityFactor * SILVER_PRICE_USD_PER_GRAM
    })

    // 5. Money Owed To Me Total
    const totalOwedToMe =
      state.loansGiven + state.expectedReceivables + state.securityDeposits

    // 6. Business Assets Total
    const totalBusiness =
      state.businessInventory + state.businessReceivables + state.businessCash

    // 7. Gross Assets
    const grossAssets =
      totalCash +
      totalInvestments +
      totalRetirement +
      totalGoldValue +
      totalSilverValue +
      totalOwedToMe +
      totalBusiness

    // 8. Liabilities (Debts)
    const totalLiabilities =
      state.creditCardDebt +
      state.studentLoans +
      state.carLoans +
      state.mortgage +
      state.personalLoans +
      state.medicalBills +
      state.otherDebts

    // 9. Net Zakatable Assets
    const netAssets = Math.max(0, grossAssets - totalLiabilities)

    // 10. Both Nisab Thresholds
    const goldNisabValue = GOLD_NISAB_GRAMS * GOLD_PRICE_USD_PER_GRAM
    const silverNisabValue = SILVER_NISAB_GRAMS * SILVER_PRICE_USD_PER_GRAM

    // Current nisab based on user selection
    const currentNisabValue =
      state.nisabStandard === 'gold' ? goldNisabValue : silverNisabValue

    // 11. Zakat calculations for both standards
    const isZakatDueGold = netAssets >= goldNisabValue
    const isZakatDueSilver = netAssets >= silverNisabValue
    const zakatAmountGold = isZakatDueGold ? netAssets * ZAKAT_RATE : 0
    const zakatAmountSilver = isZakatDueSilver ? netAssets * ZAKAT_RATE : 0

    // Current values based on selection
    const isZakatDue =
      state.nisabStandard === 'gold' ? isZakatDueGold : isZakatDueSilver
    const zakatAmount =
      state.nisabStandard === 'gold' ? zakatAmountGold : zakatAmountSilver

    return {
      totalCash,
      totalInvestments,
      totalRetirement,
      totalGoldValue,
      totalSilverValue,
      totalOwedToMe,
      totalBusiness,
      grossAssets,
      totalLiabilities,
      netAssets,
      // Both nisab values
      goldNisabValue,
      silverNisabValue,
      currentNisabValue,
      // Both zakat calculations
      isZakatDueGold,
      isZakatDueSilver,
      zakatAmountGold,
      zakatAmountSilver,
      // Current selection
      isZakatDue,
      zakatAmount,
    }
  }, [state])

  return {
    currentStepIndex,
    currentStep,
    steps,
    isFirstStep,
    isLastStep,
    nextStep,
    prevStep,
    goToStep,
    state,
    updateState,
    resetWizard,
    totals,
    constants: {
      GOLD_PRICE_USD_PER_GRAM,
      SILVER_PRICE_USD_PER_GRAM,
      GOLD_NISAB_GRAMS,
      SILVER_NISAB_GRAMS,
      ZAKAT_RATE,
    },
  }
}
